/**
 * `/o/:org/toolkits` - grouped skills, and what a toolkit does not grant.
 *
 * V1 section 9 asks for cards with name, purpose, owner and a usable-skill
 * count, a detail view listing contents, and an explanation that access to a
 * toolkit is separate from membership in it. That last sentence is a security
 * property (ACL-02), so it is stated on the screen rather than left to a
 * tooltip.
 *
 * The count is the number of items this caller may see. `can_see_toolkit_item`
 * filters both the items and the skills behind them, so a smaller number is a
 * correct answer rather than a missing row.
 */

import type { AppShell, ScreenState } from '../app.js';
import * as api from '../api/queries.js';
import { type Result, ok } from '../api/client.js';
import { type RawHtml, html } from '../lib/html.js';
import { LIFECYCLE_LABEL, personLabel } from '../lib/format.js';
import { button, docHead, emptyState, failurePanel, tag } from '../ui/components.js';

export interface ToolkitData {
  readonly toolkits: readonly api.ToolkitRow[];
  readonly contents: Readonly<Record<string, readonly ToolkitEntry[]>>;
}

interface ToolkitEntry {
  readonly item: api.ToolkitItemRow;
  readonly skill: api.SkillRow | null;
}

export async function loadToolkits(app: AppShell, orgId: string): Promise<Result<ToolkitData>> {
  const toolkits = await api.listToolkits(app.client, orgId);
  if (!toolkits.ok) return toolkits;

  const contents: Record<string, readonly ToolkitEntry[]> = {};
  for (const toolkit of toolkits.value) {
    const items = await api.listToolkitContents(app.client, toolkit.id);
    if (!items.ok) return items;
    contents[toolkit.id] = items.value;
  }
  return ok({ toolkits: toolkits.value, contents });
}

export function renderToolkits(app: AppShell, screen: ScreenState): RawHtml {
  if (screen.kind === 'loading' || screen.kind === 'idle') {
    return html`
      ${docHead([{ label: 'Section', value: 'Toolkits' }], 'Toolkits')}
      <div class="panel"><p class="cell-note">Loading toolkits.</p></div>
    `;
  }
  if (screen.kind === 'failed') {
    return html`
      ${docHead([{ label: 'Section', value: 'Toolkits' }], 'Toolkits')}
      ${failurePanel(screen.failure, { retry: 'toolkits:reload' })}
    `;
  }

  const data = screen.data as ToolkitData;
  return html`
    ${docHead(
      [
        { label: 'Section', value: 'Toolkits' },
        { label: 'Organization', value: app.orgSlug },
      ],
      'Toolkits',
      'A toolkit is a named group of skills for discovery. Being able to see a toolkit does not give you access to the skills inside it.',
    )}

    <div class="toolbar">
      <span class="topbar-spacer"></span>
      ${button('New toolkit', 'toolkits:new', 'primary', {
        // The return address for the one modal's focus return, recorded by
        // `noteDialogReturnFocus` — see `views/actions.ts`.
        id: 'new-toolkit',
      })}
    </div>

    ${data.toolkits.length === 0
      ? html`<div class="panel">
          ${emptyState({
            title: 'No toolkits yet',
            body: 'A toolkit groups skills so a team can find the ones that apply to a job. Creating one does not change anyone access to the skills inside it.',
            primary: { label: 'New toolkit', action: 'toolkits:new', id: 'new-toolkit-empty' },
            secondary: [],
          })}
        </div>`
      : data.toolkits.map((toolkit) => toolkitCard(app, toolkit, data.contents[toolkit.id] ?? []))}
  `;
}

function toolkitCard(
  app: AppShell,
  toolkit: api.ToolkitRow,
  entries: readonly ToolkitEntry[],
): RawHtml {
  // `entry.skill !== null` is the load-bearing clause, and it was missing.
  //
  // `entry.skill?.current_release_id !== null` is TRUE when `entry.skill` is
  // null: the optional chain yields `undefined`, and `undefined !== null`.
  // `loadToolkits` maps a skill this caller may not read to `null` precisely so
  // it is not rendered (`listToolkitContents`, `queries.ts:1074` — "dropped
  // rather than reported as a broken toolkit"), and the row list honours that
  // with an "A skill you cannot see" line at :118. The COUNT did not.
  //
  // Measured before this line was corrected, by rendering one toolkit twice —
  // once as a manager who sees all three skills and once as a member for whom
  // two of the three are withheld:
  //
  //     MANAGER    usable= 3 of= 3 of 3 listed
  //     RESTRICTED usable= 3 of= 3 of 3 listed
  //
  // Two callers with different authority, and the LESS authorized one was told
  // it could use three skills including the two it cannot read. That is V1 P3's
  // gate sentence ("counts and status badges come from actual authorized data",
  // `docs/V1-IMPLEMENTATION.md:57`) read backwards: the number was authorized
  // for nobody. The file's own header at :10-12 states the intended rule — "the
  // count is the number of items this caller may see" — and the code did not
  // implement the rule the comment describes.
  const usable = entries.filter(
    (entry) => entry.skill !== null && entry.skill.current_release_id !== null,
  ).length;
  // Named, because the denominator needs the same honesty. Entries whose skill
  // is withheld are counted here, so the two numbers cannot be confused: `usable`
  // is skills you can use, `entries.length` is what the policy let you see.
  const withheld = entries.filter((entry) => entry.skill === null).length;
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>${toolkit.title}</h2>
        <span class="cell-meta">${usable} usable</span>
      </div>
      <div class="panel-body stack-sm">
        <p class="cell-note">${toolkit.description ?? 'No purpose recorded.'}</p>
        <dl class="dl">
          <dt>Slug</dt>
          <dd><code>${toolkit.slug}</code></dd>
          <dt>Owner</dt>
          <dd>${personLabel(toolkit.owner_id, app.state.directory)}</dd>
          <dt>Skills you can use</dt>
          <dd>
            ${usable} of ${entries.length} listed${
              withheld === 0
                ? ''
                : html` &middot; ${withheld} withheld from you by policy`
            }
          </dd>
        </dl>
        ${withheld === 0
          ? ''
          : html`<p class="cell-note">
              ${withheld} of the ${entries.length} items in this toolkit name a skill you may not read.
              They are listed so the toolkit does not look smaller than it is, and they are not counted
              as usable: being able to see a toolkit grants discovery, not access to what is inside it.
            </p>`}
        ${entries.length === 0
          ? html`<p class="cell-note">
              Nothing in this toolkit is visible to you. Toolkit membership grants discovery only, so
              an empty list here is an access answer rather than an empty toolkit.
            </p>`
          : html`<ul class="file-list">
              ${entries.map((entry) =>
                entry.skill === null
                  ? html`<li><span>A skill you cannot see</span><span>${entry.item.skill_id.slice(0, 8)}</span></li>`
                  : html`<li>
                      <span>
                        <a href="/o/${app.orgSlug}/skills/${entry.skill.id}">${entry.skill.title}</a>
                      </span>
                      <span>
                        ${tag(
                          entry.skill.current_release_id === null
                            ? 'Unpublished'
                            : (LIFECYCLE_LABEL[entry.skill.lifecycle] ?? 'Published'),
                          entry.skill.current_release_id === null ? 'U' : 'P',
                          entry.skill.current_release_id === null ? 'plain' : 'accent',
                        )}
                      </span>
                    </li>`,
              )}
            </ul>`}
      </div>
    </div>
  `;
}